import { useState, useEffect, useCallback } from 'react';
import { FiSave, FiPlus, FiEdit2, FiTrash2, FiX, FiUser, FiUsers, FiBook, FiCalendar, FiBell, FiMessageSquare } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Settings() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // location.state에서 activeTab 전달받으면 해당 탭으로 시작
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'academy');
  const [pointBalance, setPointBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectForm, setSubjectForm] = useState({ name: '', description: '' });
  const [academyForm, setAcademyForm] = useState({
    name: '',
    phone: '',
    address: '',
    operationType: 'solo',
    lessonReportConfig: {
      targetType: 'class',
      frequencies: ['daily']
    }
  });
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [notificationForm, setNotificationForm] = useState({
    attendance: {
      push: { enabled: true, checkIn: true, checkOut: true },
      alimtalk: { enabled: false, checkIn: true, checkOut: true }
    },
    payment: {
      push: { enabled: true, request: true, complete: true, refund: true },
      alimtalk: { enabled: false, request: true, complete: true, refund: true }
    },
    lesson: {
      push: { enabled: true, daily: true, weekly: true, monthly: true },
      alimtalk: { enabled: false, daily: true, weekly: true, monthly: true }
    },
    notice: {
      push: { enabled: true, holiday: true, briefing: true, special: true, schedule: true, exam: true, consult: true },
      alimtalk: { enabled: false, holiday: true, briefing: true, special: true, schedule: true, exam: true, consult: true }
    }
  });

  const loadData = useCallback(async () => {
    try {
      const [subjectsRes, pointsRes] = await Promise.all([
        api.get('/api/subjects'),
        api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }))
      ]);
      setSubjects(subjectsRes.data.subjects || []);
      setPointBalance(pointsRes.data.balance || 0);

      // Set initial form values
      if (tenant) {
        setAcademyForm({
          name: tenant.name || '',
          phone: tenant.phone || '',
          address: tenant.address || '',
          operationType: tenant.operationType || 'solo',
          lessonReportConfig: tenant.lessonReportConfig || {
            targetType: 'class',
            frequencies: ['daily']
          }
        });
        // 알림 설정 (Mongoose 스키마의 기본값 사용)
        setNotificationForm({
          attendance: {
            push: tenant.attendanceNotification?.push || {},
            alimtalk: tenant.attendanceNotification?.alimtalk || {}
          },
          payment: {
            push: tenant.paymentNotification?.push || {},
            alimtalk: tenant.paymentNotification?.alimtalk || {}
          },
          lesson: {
            push: tenant.lessonNotification?.push || {},
            alimtalk: tenant.lessonNotification?.alimtalk || {}
          },
          notice: {
            push: tenant.noticeNotification?.push || {},
            alimtalk: tenant.noticeNotification?.alimtalk || {}
          }
        });
      }
      if (user) {
        setProfileForm({
          name: user.name || '',
          phone: user.phone || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }, [user, tenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subject CRUD
  const openSubjectModal = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setSubjectForm({ name: subject.name, description: subject.description || '' });
    } else {
      setEditingSubject(null);
      setSubjectForm({ name: '', description: '' });
    }
    setShowSubjectModal(true);
  };

  const handleSubjectSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubject) {
        await api.put(`/api/subjects/${editingSubject._id}`, subjectForm);
      } else {
        await api.post('/api/subjects', subjectForm);
      }
      setShowSubjectModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const handleSubjectDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/api/subjects/${id}`);
      loadData();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  // Academy info update
  const handleAcademyUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/api/auth/academy', {
        phone: academyForm.phone,
        address: academyForm.address,
        operationType: academyForm.operationType,
        lessonReportConfig: academyForm.lessonReportConfig
      });
      alert('저장되었습니다. 변경사항을 적용하려면 페이지를 새로고침 해주세요.');
      // 페이지 새로고침하여 변경된 설정 반영
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  // Profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Basic info update
      await api.put('/api/auth/profile', {
        name: profileForm.name,
        phone: profileForm.phone
      });

      // Password change if provided
      if (profileForm.newPassword) {
        if (profileForm.newPassword !== profileForm.confirmPassword) {
          alert('새 비밀번호가 일치하지 않습니다');
          return;
        }
        await api.put('/api/auth/password', {
          currentPassword: profileForm.currentPassword,
          newPassword: profileForm.newPassword
        });
      }

      alert('저장되었습니다');
      setProfileForm({ ...profileForm, currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  // 알림 설정 업데이트
  const handleNotificationUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put('/api/auth/academy', {
        attendanceNotification: notificationForm.attendance,
        paymentNotification: notificationForm.payment,
        lessonNotification: notificationForm.lesson,
        noticeNotification: notificationForm.notice
      });
      alert('알림 설정이 저장되었습니다.');
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  // 푸시 활성화 토글 (포인트 체크 - 10P)
  const handlePushToggle = (category, enabled) => {
    if (enabled && pointBalance < 10) {
      if (window.confirm('푸시 알림 발송에는 포인트가 필요합니다. (건당 10P)\n\n포인트를 충전하시겠습니까?')) {
        navigate('/points');
      }
      return;
    }
    setNotificationForm({
      ...notificationForm,
      [category]: { ...notificationForm[category], push: { ...notificationForm[category].push, enabled } }
    });
  };

  // 알림톡 활성화 토글 (포인트 체크 - 30P)
  const handleAlimtalkToggle = (category, enabled) => {
    if (enabled && pointBalance < 30) {
      if (window.confirm('알림톡 발송에는 포인트가 필요합니다. (건당 30P)\n\n포인트를 충전하시겠습니까?')) {
        navigate('/points');
      }
      return;
    }
    setNotificationForm({
      ...notificationForm,
      [category]: { ...notificationForm[category], alimtalk: { ...notificationForm[category].alimtalk, enabled } }
    });
  };

  if (loading) {
    return <div className="loading-page">로딩 중...</div>;
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>설정</h2>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'academy' ? 'active' : ''}`}
          onClick={() => setActiveTab('academy')}
        >
          학원 정보
        </button>
        <button
          className={`tab ${activeTab === 'notification' ? 'active' : ''}`}
          onClick={() => setActiveTab('notification')}
        >
          알림 설정
        </button>
        <button
          className={`tab ${activeTab === 'subjects' ? 'active' : ''}`}
          onClick={() => setActiveTab('subjects')}
        >
          과목 관리
        </button>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          내 계정
        </button>
      </div>

      {/* Academy Info Tab */}
      {activeTab === 'academy' && (
        <div className="settings-section">
          <h3>학원 정보</h3>
          <form className="settings-form" onSubmit={handleAcademyUpdate}>
            <div className="form-group">
              <label>학원명</label>
              <input
                type="text"
                value={academyForm.name}
                onChange={(e) => setAcademyForm({ ...academyForm, name: e.target.value })}
                disabled
              />
              <small className="text-muted">학원명 변경은 고객센터에 문의하세요</small>
            </div>
            <div className="form-group">
              <label>연락처</label>
              <input
                type="tel"
                value={academyForm.phone}
                onChange={(e) => setAcademyForm({ ...academyForm, phone: e.target.value })}
                placeholder="02-1234-5678"
              />
            </div>
            <div className="form-group">
              <label>주소</label>
              <input
                type="text"
                value={academyForm.address}
                onChange={(e) => setAcademyForm({ ...academyForm, address: e.target.value })}
                placeholder="학원 주소"
              />
            </div>

            <div className="form-group">
              <label>운영 체제</label>
              <div className="operation-type-selector" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <div
                  className={`operation-card ${academyForm.operationType === 'solo' ? 'selected' : ''}`}
                  onClick={() => setAcademyForm({ ...academyForm, operationType: 'solo' })}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiUser size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>1인 체제</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      원장님 혼자 운영
                    </p>
                  </div>
                </div>
                <div
                  className={`operation-card ${academyForm.operationType === 'team' ? 'selected' : ''}`}
                  onClick={() => setAcademyForm({ ...academyForm, operationType: 'team' })}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiUsers size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>다인 체제</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      원장님 + 강사진
                    </p>
                  </div>
                </div>
              </div>
              <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                * 운영 체제를 변경하면 메뉴와 대시보드 위젯이 달라집니다
              </small>
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

            <h4 style={{ marginBottom: '16px', color: '#374151' }}>수업일지 설정</h4>

            <div className="form-group">
              <label>작성 대상</label>
              <div className="operation-type-selector" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <div
                  className={`operation-card ${academyForm.lessonReportConfig.targetType === 'class' ? 'selected' : ''}`}
                  onClick={() => setAcademyForm({
                    ...academyForm,
                    lessonReportConfig: { ...academyForm.lessonReportConfig, targetType: 'class' }
                  })}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiUsers size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>반별</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      반 단위로 작성
                    </p>
                  </div>
                </div>
                <div
                  className={`operation-card ${academyForm.lessonReportConfig.targetType === 'student' ? 'selected' : ''}`}
                  onClick={() => setAcademyForm({
                    ...academyForm,
                    lessonReportConfig: { ...academyForm.lessonReportConfig, targetType: 'student' }
                  })}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiUser size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>학생별</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      학생 개별 작성
                    </p>
                  </div>
                </div>
              </div>
              <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                * 학생별: 1:1 개별맞춤 수업에 적합
              </small>
            </div>

            <div className="form-group">
              <label>작성 주기 (다중 선택 가능)</label>
              <div className="operation-type-selector" style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <div
                  className={`operation-card ${academyForm.lessonReportConfig.frequencies?.includes('daily') ? 'selected' : ''}`}
                  onClick={() => {
                    const current = academyForm.lessonReportConfig.frequencies || [];
                    const updated = current.includes('daily')
                      ? current.filter(f => f !== 'daily')
                      : [...current, 'daily'];
                    setAcademyForm({
                      ...academyForm,
                      lessonReportConfig: { ...academyForm.lessonReportConfig, frequencies: updated }
                    });
                  }}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiCalendar size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>일일</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      매 수업마다 작성
                    </p>
                  </div>
                </div>
                <div
                  className={`operation-card ${academyForm.lessonReportConfig.frequencies?.includes('weekly') ? 'selected' : ''}`}
                  onClick={() => {
                    const current = academyForm.lessonReportConfig.frequencies || [];
                    const updated = current.includes('weekly')
                      ? current.filter(f => f !== 'weekly')
                      : [...current, 'weekly'];
                    setAcademyForm({
                      ...academyForm,
                      lessonReportConfig: { ...academyForm.lessonReportConfig, frequencies: updated }
                    });
                  }}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiBook size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>주간</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      주간 리포트 형태
                    </p>
                  </div>
                </div>
                <div
                  className={`operation-card ${academyForm.lessonReportConfig.frequencies?.includes('monthly') ? 'selected' : ''}`}
                  onClick={() => {
                    const current = academyForm.lessonReportConfig.frequencies || [];
                    const updated = current.includes('monthly')
                      ? current.filter(f => f !== 'monthly')
                      : [...current, 'monthly'];
                    setAcademyForm({
                      ...academyForm,
                      lessonReportConfig: { ...academyForm.lessonReportConfig, frequencies: updated }
                    });
                  }}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  <FiBook size={24} />
                  <div style={{ marginTop: '8px' }}>
                    <strong>월간</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      월간 리포트 형태
                    </p>
                  </div>
                </div>
              </div>
              <small className="text-muted" style={{ display: 'block', marginTop: '8px' }}>
                * 다중 선택 가능: 일일, 주간, 월간 중 필요한 것만 선택하세요
              </small>
            </div>

            <button type="submit" className="btn btn-primary">
              <FiSave /> 저장
            </button>
          </form>
        </div>
      )}

      {/* Notification Settings Tab */}
      {activeTab === 'notification' && (
        <div className="settings-section">
          <h3>알림 설정</h3>
          <p style={{ color: '#6B7280', marginBottom: '24px' }}>
            각 상황별로 학부모에게 보낼 알림을 설정할 수 있습니다.
          </p>

          <form className="settings-form" onSubmit={handleNotificationUpdate}>
            {/* ===== 출결 알림 ===== */}
            <h4 style={{ marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiBell size={20} /> 출결 알림
            </h4>

            {/* 푸시 알림 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              backgroundColor: notificationForm.attendance.push.enabled ? '#EFF6FF' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiBell size={24} color={notificationForm.attendance.push.enabled ? '#3B82F6' : '#9CA3AF'} />
                  <div>
                    <strong>푸시 알림</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      앱 푸시 알림 (건당 10P 차감)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.attendance.push.enabled}
                    onChange={(e) => handlePushToggle('attendance', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.attendance.push.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.attendance.push.checkIn}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          attendance: {
                            ...notificationForm.attendance,
                            push: { ...notificationForm.attendance.push, checkIn: e.target.checked }
                          }
                        })}
                      />
                      입실 알림
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.attendance.push.checkOut}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          attendance: {
                            ...notificationForm.attendance,
                            push: { ...notificationForm.attendance.push, checkOut: e.target.checked }
                          }
                        })}
                      />
                      퇴실 알림
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#DBEAFE', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#1E40AF', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 100 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 알림톡 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              backgroundColor: notificationForm.attendance.alimtalk.enabled ? '#FEF3C7' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiMessageSquare size={24} color={notificationForm.attendance.alimtalk.enabled ? '#F59E0B' : '#9CA3AF'} />
                  <div>
                    <strong>알림톡</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      카카오 알림톡 (건당 30P 차감, 푸시알림 포함)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.attendance.alimtalk.enabled}
                    onChange={(e) => handleAlimtalkToggle('attendance', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.attendance.alimtalk.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.attendance.alimtalk.checkIn}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          attendance: {
                            ...notificationForm.attendance,
                            alimtalk: { ...notificationForm.attendance.alimtalk, checkIn: e.target.checked }
                          }
                        })}
                      />
                      입실 알림
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.attendance.alimtalk.checkOut}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          attendance: {
                            ...notificationForm.attendance,
                            alimtalk: { ...notificationForm.attendance.alimtalk, checkOut: e.target.checked }
                          }
                        })}
                      />
                      퇴실 알림
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#FEF9C3', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#92400E', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 300 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

            {/* ===== 수납 알림 ===== */}
            <h4 style={{ marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiBell size={20} /> 수납 알림
            </h4>

            {/* 푸시 알림 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              backgroundColor: notificationForm.payment.push.enabled ? '#EFF6FF' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiBell size={24} color={notificationForm.payment.push.enabled ? '#3B82F6' : '#9CA3AF'} />
                  <div>
                    <strong>푸시 알림</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      앱 푸시 알림 (건당 10P 차감)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.payment.push.enabled}
                    onChange={(e) => handlePushToggle('payment', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.payment.push.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.push.request}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            push: { ...notificationForm.payment.push, request: e.target.checked }
                          }
                        })}
                      />
                      수납 요청
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.push.complete}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            push: { ...notificationForm.payment.push, complete: e.target.checked }
                          }
                        })}
                      />
                      수납 완료
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.push.refund}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            push: { ...notificationForm.payment.push, refund: e.target.checked }
                          }
                        })}
                      />
                      환불 완료
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#DBEAFE', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#1E40AF', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 100 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 알림톡 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              backgroundColor: notificationForm.payment.alimtalk.enabled ? '#FEF3C7' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiMessageSquare size={24} color={notificationForm.payment.alimtalk.enabled ? '#F59E0B' : '#9CA3AF'} />
                  <div>
                    <strong>알림톡</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      카카오 알림톡 (건당 30P 차감, 푸시알림 포함)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.payment.alimtalk.enabled}
                    onChange={(e) => handleAlimtalkToggle('payment', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.payment.alimtalk.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.alimtalk.request}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            alimtalk: { ...notificationForm.payment.alimtalk, request: e.target.checked }
                          }
                        })}
                      />
                      수납 요청
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.alimtalk.complete}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            alimtalk: { ...notificationForm.payment.alimtalk, complete: e.target.checked }
                          }
                        })}
                      />
                      수납 완료
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.payment.alimtalk.refund}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          payment: {
                            ...notificationForm.payment,
                            alimtalk: { ...notificationForm.payment.alimtalk, refund: e.target.checked }
                          }
                        })}
                      />
                      환불 완료
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#FEF9C3', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#92400E', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 300 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

            {/* ===== 수업일지 알림 ===== */}
            <h4 style={{ marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiBell size={20} /> 수업일지 알림
            </h4>

            {/* 푸시 알림 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              backgroundColor: notificationForm.lesson.push.enabled ? '#EFF6FF' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiBell size={24} color={notificationForm.lesson.push.enabled ? '#3B82F6' : '#9CA3AF'} />
                  <div>
                    <strong>푸시 알림</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      앱 푸시 알림 (건당 10P 차감)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.lesson.push.enabled}
                    onChange={(e) => handlePushToggle('lesson', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.lesson.push.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.push.daily}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            push: { ...notificationForm.lesson.push, daily: e.target.checked }
                          }
                        })}
                      />
                      일일 리포트
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.push.weekly}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            push: { ...notificationForm.lesson.push, weekly: e.target.checked }
                          }
                        })}
                      />
                      주간 리포트
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.push.monthly}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            push: { ...notificationForm.lesson.push, monthly: e.target.checked }
                          }
                        })}
                      />
                      월간 리포트
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#DBEAFE', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#1E40AF', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 100 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 알림톡 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              backgroundColor: notificationForm.lesson.alimtalk.enabled ? '#FEF3C7' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiMessageSquare size={24} color={notificationForm.lesson.alimtalk.enabled ? '#F59E0B' : '#9CA3AF'} />
                  <div>
                    <strong>알림톡</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      카카오 알림톡 (건당 30P 차감, 푸시알림 포함)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.lesson.alimtalk.enabled}
                    onChange={(e) => handleAlimtalkToggle('lesson', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.lesson.alimtalk.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.alimtalk.daily}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            alimtalk: { ...notificationForm.lesson.alimtalk, daily: e.target.checked }
                          }
                        })}
                      />
                      일일 리포트
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.alimtalk.weekly}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            alimtalk: { ...notificationForm.lesson.alimtalk, weekly: e.target.checked }
                          }
                        })}
                      />
                      주간 리포트
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.lesson.alimtalk.monthly}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          lesson: {
                            ...notificationForm.lesson,
                            alimtalk: { ...notificationForm.lesson.alimtalk, monthly: e.target.checked }
                          }
                        })}
                      />
                      월간 리포트
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#FEF9C3', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#92400E', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 300 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid #E5E7EB' }} />

            {/* ===== 공지 알림 ===== */}
            <h4 style={{ marginBottom: '16px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiBell size={20} /> 공지 알림
            </h4>

            {/* 푸시 알림 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              backgroundColor: notificationForm.notice.push.enabled ? '#EFF6FF' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiBell size={24} color={notificationForm.notice.push.enabled ? '#3B82F6' : '#9CA3AF'} />
                  <div>
                    <strong>푸시 알림</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      앱 푸시 알림 (건당 10P 차감)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.notice.push.enabled}
                    onChange={(e) => handlePushToggle('notice', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.notice.push.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.holiday}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, holiday: e.target.checked }
                          }
                        })}
                      />
                      휴원 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.briefing}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, briefing: e.target.checked }
                          }
                        })}
                      />
                      설명회
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.special}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, special: e.target.checked }
                          }
                        })}
                      />
                      특강 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.schedule}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, schedule: e.target.checked }
                          }
                        })}
                      />
                      수업일정
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.exam}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, exam: e.target.checked }
                          }
                        })}
                      />
                      시험 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.push.consult}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            push: { ...notificationForm.notice.push, consult: e.target.checked }
                          }
                        })}
                      />
                      상담 안내
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#DBEAFE', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#1E40AF', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 100 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* 알림톡 설정 */}
            <div className="notification-card" style={{
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              backgroundColor: notificationForm.notice.alimtalk.enabled ? '#FEF3C7' : '#fff'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FiMessageSquare size={24} color={notificationForm.notice.alimtalk.enabled ? '#F59E0B' : '#9CA3AF'} />
                  <div>
                    <strong>알림톡</strong>
                    <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: '4px 0 0 0' }}>
                      카카오 알림톡 (건당 30P 차감, 푸시알림 포함)
                    </p>
                  </div>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={notificationForm.notice.alimtalk.enabled}
                    onChange={(e) => handleAlimtalkToggle('notice', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {notificationForm.notice.alimtalk.enabled && (
                <>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginLeft: '36px', marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.holiday}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, holiday: e.target.checked }
                          }
                        })}
                      />
                      휴원 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.briefing}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, briefing: e.target.checked }
                          }
                        })}
                      />
                      설명회
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.special}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, special: e.target.checked }
                          }
                        })}
                      />
                      특강 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.schedule}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, schedule: e.target.checked }
                          }
                        })}
                      />
                      수업일정
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.exam}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, exam: e.target.checked }
                          }
                        })}
                      />
                      시험 안내
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notificationForm.notice.alimtalk.consult}
                        onChange={(e) => setNotificationForm({
                          ...notificationForm,
                          notice: {
                            ...notificationForm.notice,
                            alimtalk: { ...notificationForm.notice.alimtalk, consult: e.target.checked }
                          }
                        })}
                      />
                      상담 안내
                    </label>
                  </div>
                  <div style={{ marginLeft: '36px', padding: '12px', backgroundColor: '#FEF9C3', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.85rem', color: '#92400E', margin: 0 }}>
                      현재 포인트: <strong>{pointBalance.toLocaleString()}P</strong>
                      {pointBalance < 300 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          style={{ marginLeft: '12px' }}
                          onClick={() => navigate('/points')}
                        >
                          충전하기
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}
            </div>

            <button type="submit" className="btn btn-primary">
              <FiSave /> 저장
            </button>
          </form>
        </div>
      )}

      {/* Subjects Tab */}
      {activeTab === 'subjects' && (
        <div className="settings-section">
          <div className="section-header">
            <h3>과목 관리</h3>
            <button className="btn btn-primary btn-sm" onClick={() => openSubjectModal()}>
              <FiPlus /> 과목 추가
            </button>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>과목명</th>
                  <th>설명</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {subjects.length === 0 ? (
                  <tr><td colSpan="3" className="empty">등록된 과목이 없습니다</td></tr>
                ) : (
                  subjects.map((subject) => (
                    <tr key={subject._id}>
                      <td>{subject.name}</td>
                      <td>{subject.description || '-'}</td>
                      <td>
                        <button className="icon-btn" onClick={() => openSubjectModal(subject)}>
                          <FiEdit2 />
                        </button>
                        <button className="icon-btn danger" onClick={() => handleSubjectDelete(subject._id)}>
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="settings-section">
          <h3>내 계정</h3>
          <form className="settings-form" onSubmit={handleProfileUpdate}>
            <div className="form-group">
              <label>이메일</label>
              <input type="email" value={user?.email || ''} disabled />
            </div>
            <div className="form-group">
              <label>이름</label>
              <input
                type="text"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>연락처</label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                placeholder="010-1234-5678"
              />
            </div>

            <hr />
            <h4>비밀번호 변경</h4>

            <div className="form-group">
              <label>현재 비밀번호</label>
              <input
                type="password"
                value={profileForm.currentPassword}
                onChange={(e) => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
                placeholder="현재 비밀번호"
              />
            </div>
            <div className="form-group">
              <label>새 비밀번호</label>
              <input
                type="password"
                value={profileForm.newPassword}
                onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                placeholder="새 비밀번호"
              />
            </div>
            <div className="form-group">
              <label>새 비밀번호 확인</label>
              <input
                type="password"
                value={profileForm.confirmPassword}
                onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                placeholder="새 비밀번호 확인"
              />
            </div>

            <button type="submit" className="btn btn-primary">
              <FiSave /> 저장
            </button>
          </form>
        </div>
      )}

      {/* Subject Modal */}
      {showSubjectModal && (
        <div className="modal-overlay" onClick={() => setShowSubjectModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSubject ? '과목 수정' : '과목 추가'}</h3>
              <button className="close-btn" onClick={() => setShowSubjectModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubjectSubmit}>
              <div className="form-group">
                <label>과목명 *</label>
                <input
                  type="text"
                  value={subjectForm.name}
                  onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })}
                  placeholder="예: 수학, 영어"
                  required
                />
              </div>
              <div className="form-group">
                <label>설명</label>
                <input
                  type="text"
                  value={subjectForm.description}
                  onChange={(e) => setSubjectForm({ ...subjectForm, description: e.target.value })}
                  placeholder="과목 설명 (선택)"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSubjectModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSubject ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
